import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaSailboat, FaCar, FaCaravan, FaBus, FaPersonWalking, FaPersonRunning, FaMotorcycle, FaBicycle, FaHouse, FaPlane, FaTrain } from "react-icons/fa6";
import { parrotBlue, parrotDarkBlue, parrotGreen, parrotGreyTransparent, parrotTextDarkBlue, vehicleColors } from "../styles/colors";
import DOMPurify from "dompurify";
import whiteegg from "../assets/images/whiteegg.png";
import silveregg from "../assets/images/silveregg.png";
import goldenegg from "../assets/images/goldenegg.png";

const eggConfig = {
  1: { image: whiteegg, background: "#e8e8e8" },
  2: { image: silveregg, background: "#b0b7c3" },
  3: { image: goldenegg, background: "#FFD700" },
};

const apiUrl = process.env.REACT_APP_API_URL;
const voyageBaseUrl = ``;

const dotIcons = {
  0: <FaSailboat />,
  1: <FaCar />,
  2: <FaCaravan />,
  3: <FaBus />,
  4: <FaPersonWalking />,
  5: <FaPersonRunning />,
  6: <FaMotorcycle />,
  7: <FaBicycle />,
  8: <FaHouse />,
  9: <FaPlane />,
  10: <FaTrain />,
};

export function MainPageVoyageCard({ cardData, panToLocation, cardIndex }) {
  const dark = useSelector((state) => state.users.isDarkMode);
  const navigate = useNavigate();
  const handleCardClick = (voyageId) => {
    navigate(`/voyage-details/${voyageId}`);
  };

  const egg = cardData.placeType > 0 ? eggConfig[cardData.placeType] : null;
  const vehicleColor = vehicleColors[cardData.vehicleType] ?? parrotBlue;

  return (
    <div className="card" style={cardContainerStyle(dark)}>
      {egg && (
        <div style={{ ...eggBadgeClip, backgroundColor: egg.background }}>
          <img src={egg.image} alt="" style={eggBadgeImg} />
        </div>
      )}
      <div className="card-image" style={cardImageStyle}>
        <img src={voyageBaseUrl + (cardData.profileImageThumbnail || cardData.profileImage)} style={imageStyle} alt="" />
      </div>
      <div className="card-content" style={{ ...cardContentStyle, position: "relative" }}>
        {!egg && (
          <div style={{ ...vehicleColorCircle, backgroundColor: vehicleColor }}>
            <span style={{ color: "white", opacity: 0.5, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11 }}>
              {dotIcons[cardData.vehicleType]}
            </span>
          </div>
        )}
        <div style={cardTitleStyle(dark)}>{cardData.name}</div>

        {/* DETAILS */}
        <div style={cardDescriptionStyle}>
          <div
            style={{
              marginTop: "0.2rem",
              marginBottom: "0.2rem",
            }}
          >
            <span style={{ ...voyageDetailSpan(dark), marginRight: "0.5rem", }}>
              <VehicleIcon vehicleType={cardData.vehicleType} />
              {" "}{cardData.vehicle?.name}
            </span>
            <span style={voyageDetailSpan(dark)}>
              👨‍👨‍👦‍👦
              {" "}{cardData.vacancy}
            </span>
          </div>
          <span style={voyageDetailSpan(dark)}>
            📅 {formatCustomDate(cardData.startDate)} - {" "}
            {formatCustomDate(cardData.endDate)}
          </span>
        </div>

        {/* BRIEF */}

        <div
          style={cardBriefStyle(dark)}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize((cardData.brief || "").replace(/<p[^>]*>/gi, "").replace(/<\/p>/gi, " ").trim())
          }}
        />

        {/* BUTTONS */}
        <div className="card-buttons" style={buttonContainerStyle}>
          <button onClick={() => handleCardClick(cardData.id)}
            style={{ ...buttonStyle, backgroundColor: dark ? "rgba(255,255,255,0.08)" : "#00336615", color: dark ? "rgba(255,255,255,0.85)" : parrotDarkBlue, boxShadow: "none" }}>
            Trip Details
          </button>
          <button
            onClick={() =>
              panToLocation(
                cardData.waypoints[0].latitude,
                cardData.waypoints[0].longitude
              )
            }
            style={{ ...buttonStyle, backgroundColor: dark ? "rgba(255,255,255,0.08)" : "#00336615", color: dark ? "rgba(255,255,255,0.85)" : parrotDarkBlue, boxShadow: "none" }}>
            See on Map
          </button>
        </div>
      </div>
    </div>
  );
}

const vehicles = [
  "⛵", // Boat
  "🚗", // Car
  "🚐", // Caravan
  "🚌", // Bus
  "🚶", // Walk
  "🏃", // Run
  "🏍️", // Motorcycle
  "🚲", // Bicycle
  "🏠", // Tinyhouse
  "✈️", // Airplane
  "🚄", // Train
];

export default function VehicleIcon({ vehicleType }) {
  const getVehicleEmoji = (typeIndex) => {
    if (typeIndex >= 0 && typeIndex < vehicles.length) {
      return vehicles[typeIndex];
    }
    return "❓";
  };

  return (
    <span style={{ textAlign: "center" }}>{getVehicleEmoji(vehicleType)}</span>
  );
}

const voyageDetailSpan = (dark) => ({
  backgroundColor: dark ? "rgba(255,255,255,0.1)" : "rgba(0, 119, 234,0.1)",
  color: dark ? "rgba(255,255,255,0.85)" : "#007bff",
  borderRadius: "1rem",
  padding: "0.1rem",
  paddingLeft: "1rem",
  paddingRight: "1rem",
});

const vehicleColorCircle = {
  position: "absolute",
  top: "0.5rem",
  right: "0.5rem",
  width: 21,
  height: 21,
  borderRadius: "50%",
  zIndex: 10,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const eggBadgeClip = {
  position: "absolute",
  top: "16rem",
  right: 8,
  width: 34,
  height: 34,
  borderRadius: "50%",
  zIndex: 10,
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const eggBadgeImg = {
  width: 34,
  height: 38,
  objectFit: "contain",
};

const cardContainerStyle = (dark) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  borderRadius: "8px",
  overflow: "hidden",
  width: "24rem",
  maxWidth: "600px",
  maxHeight: "700px",
  backgroundColor: dark ? "#011a32" : "#fff",
  margin: "1rem",
  boxShadow: `0 4px 6px rgba(0, 0, 0, 0.3), inset 0 -8px 6px rgba(0, 0, 0, 0.1)`,
});

const imageStyle = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
};

const cardImageStyle = {
  width: "100%",
  height: "16rem",
  objectFit: "cover",
  borderBottom: "1px solid #ddd",
};

const cardContentStyle = {
  display: "flex",
  height: "16rem",
  flexDirection: "column",
  boxShadow: `
  0 4px 6px rgba(0, 0, 0, 0.4),
  inset 0 -6px 6px rgba(0, 0, 0, 0.4)
`,
};

const cardTitleStyle = (dark) => ({
  fontSize: "1.3rem",
  fontWeight: "bold",
  color: dark ? "#2ac898" : "rgba(10, 119, 234,1)",
});

const cardBriefStyle = (dark) => ({
  fontSize: "1.1rem",
  fontFamily: "Nunito, sans-serif",
  fontWeight: "600",
  color: dark ? "rgba(255,255,255,0.8)" : parrotTextDarkBlue,
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  WebkitLineClamp: 4,
  textOverflow: "ellipsis",
  paddingLeft: "1rem",
  paddingRight: "1rem",
  lineHeight: "1.65rem",
  letterSpacing: "0.015em",
  marginTop: "0.5rem",
});

const cardDescriptionStyle = {
  fontSize: "1rem",
  color: "blue",
  fontWeight: "600",
};

const buttonContainerStyle = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "1rem",
  marginTop: "auto",
  paddingBottom: "1rem",
};

const buttonStyle = {
  width: "35%", // Match the input width
  padding: "0.2rem",
  borderRadius: "1.5rem",
  textAlign: "center",
  color: "white",
  fontWeight: "bold",
  cursor: "pointer",
  fontSize: "1rem",
  border: "none",
  boxShadow: `
      0 4px 6px rgba(0, 0, 0, 0.3),
      inset 0 -4px 6px rgba(0, 0, 0, 0.3)
    `,
  transition: "box-shadow 0.2s ease",
  WebkitFontSmoothing: "antialiased",
  MozOsxFontSmoothing: "grayscale",
};


function formatCustomDate(dateString) {
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
  }).format(new Date(dateString));
}

